import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle, Shield, Sparkles } from 'lucide-react';
import api, { LOCATIONS, PROJECT_TYPES, formatZARFull, trackEvent } from '@/lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
  { id: 'orientation', title: 'Welcome', subtitle: 'Setting the stage' },
  { id: 'spatial_intent', title: 'Spatial Intent', subtitle: 'How you experience space' },
  { id: 'lifestyle', title: 'Lifestyle', subtitle: 'What matters most' },
  { id: 'constraints', title: 'Trade-offs', subtitle: 'Understanding your options' },
  { id: 'readiness', title: 'Readiness', subtitle: 'How prepared are you' },
  { id: 'summary', title: 'Summary', subtitle: 'Your architect-ready output' },
];

const DISCLAIMER = "This guided consultation prepares you for working with an architect. It does not produce designs or replace professional architectural judgment. Final decisions require a registered architect (SACAP).";

export default function AgenticArchitect() {
  const [step, setStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [tradeoffs, setTradeoffs] = useState(null);
  const [spatialSignals, setSpatialSignals] = useState([]);
  const [readinessData, setReadinessData] = useState(null);

  const [form, setForm] = useState({
    project_type: '', location: '', budget: '',
    // Spatial Intent
    important_spaces: '', lacking_space: '', openness_preference: '',
    // Lifestyle
    daily_routine: '', entertaining_style: '', work_style: '', outdoor_priority: '',
    // Readiness
    has_site: false, has_budget: false, has_timeline: false, has_brief: false, has_inspiration: false,
    // Contact
    email: '', name: '',
  });

  useEffect(() => { trackEvent('page_view', { page: 'agentic_architect' }); }, []);

  // Pre-fetch trade-offs when entering constraints step
  useEffect(() => {
    if (step === 3 && form.budget && !tradeoffs) {
      fetchTradeoffs();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTradeoffs = async () => {
    try {
      const { data: config } = await api.get('/config');
      const budget = Number(form.budget);
      const location = form.location || 'johannesburg';
      const locData = config.location_multipliers?.[location] || config.location_multipliers?.other || { multiplier: 1 };
      const locMult = typeof locData === 'object' ? locData.multiplier : locData;
      const tradeoffData = {};
      for (const [finish, fdata] of Object.entries(config.cost_per_sqm || {})) {
        const avgCost = ((fdata.min + fdata.max) / 2) * locMult * 1.12;
        tradeoffData[finish] = {
          affordable_sqm: budget && avgCost ? Math.round(budget / avgCost * 10) / 10 : 0,
          cost_per_sqm: Math.round(avgCost),
        };
      }
      setTradeoffs(tradeoffData);
    } catch {}
  };

  const update = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const startSession = async () => {
    if (!form.project_type || !form.location) {
      toast.error('Please select project type and location');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/architect/start', {
        project_type: form.project_type, location: form.location,
        budget: form.budget ? Number(form.budget) : null,
        email: form.email || undefined, name: form.name || undefined,
      });
      setSessionId(data.session_id);
      setStep(1);
      trackEvent('architect_session_started');
    } catch { toast.error('Failed to start session'); }
    finally { setLoading(false); }
  };

  const submitStep = async (stepId) => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const stepResponses = {};
      if (stepId === 'spatial_intent') {
        stepResponses.important_spaces = form.important_spaces;
        stepResponses.lacking_space = form.lacking_space;
        stepResponses.openness_preference = form.openness_preference;
      } else if (stepId === 'lifestyle') {
        stepResponses.daily_routine = form.daily_routine;
        stepResponses.entertaining_style = form.entertaining_style;
        stepResponses.work_style = form.work_style;
        stepResponses.outdoor_priority = form.outdoor_priority;
      } else if (stepId === 'constraints') {
        stepResponses.budget = form.budget ? Number(form.budget) : 0;
        stepResponses.size_priority = form.size_priority;
        stepResponses.finish_priority = form.finish_priority;
      } else if (stepId === 'readiness') {
        stepResponses.has_site = form.has_site;
        stepResponses.has_budget = form.has_budget;
        stepResponses.has_timeline = form.has_timeline;
        stepResponses.has_brief = form.has_brief;
        stepResponses.has_inspiration = form.has_inspiration;
      }

      const { data } = await api.post('/architect/respond', {
        session_id: sessionId, step_id: stepId, responses: stepResponses,
      });

      if (data.spatial_signals) setSpatialSignals(data.spatial_signals);
      if (data.constraints?.tradeoffs) setTradeoffs(data.constraints.tradeoffs);
      if (data.readiness_scores) setReadinessData(data.readiness_scores);

      setStep(s => s + 1);
      trackEvent('architect_step_completed', { step: stepId });
    } catch { toast.error('Failed to process. Please try again.'); }
    finally { setLoading(false); }
  };

  const completeSession = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const { data } = await api.post('/architect/complete', { session_id: sessionId });
      setSummary(data.summary);
      if (data.spatial_signals) setSpatialSignals(data.spatial_signals);
      if (data.readiness) setReadinessData(data.readiness);
      trackEvent('architect_session_completed');
      toast.success('Architect-ready summary generated');
    } catch { toast.error('Failed to generate summary'); }
    finally { setLoading(false); }
  };

  const progress = ((step) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="architect-page">
      {/* Header */}
      <div className="section-dark py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Guided Consultation</p>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tighter font-medium text-white mb-4">
            Prepare for Architecture
          </h1>
          <p className="text-white/50 font-body text-base max-w-2xl mb-6">
            A guided pre-design collaboration. We'll help you clarify your needs, priorities, and constraints — so when you work with an architect, the process is faster, clearer, and more cost-effective.
          </p>
          <div className="flex items-start gap-2 text-white/30 text-xs font-body">
            <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brand-accent/60" />
            <span>{DISCLAIMER}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Progress */}
        {step > 0 && step < STEPS.length - 1 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-heading uppercase tracking-wider text-brand-dark/50">
                Step {step} of {STEPS.length - 1}
              </span>
              <span className="text-xs font-heading uppercase tracking-wider text-brand-accent-dark font-semibold">
                {STEPS[step].title}
              </span>
            </div>
            <Progress value={progress} className="h-1.5" data-testid="architect-progress" />
          </div>
        )}

        <div className="bg-white rounded-sm border border-black/10 p-8" data-testid="architect-form">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>

              {/* STEP 0: Orientation */}
              {step === 0 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-2xl font-medium mb-3">Let's prepare the ground.</h2>
                    <p className="text-brand-dark/60 font-body text-base max-w-lg mx-auto leading-relaxed">
                      This guided process captures your spatial needs, lifestyle priorities, and project constraints — creating a structured foundation for your architect.
                    </p>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Project Type *</Label>
                      <Select value={form.project_type} onValueChange={(v) => update('project_type', v)}>
                        <SelectTrigger data-testid="arch-select-type" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Location *</Label>
                      <Select value={form.location} onValueChange={(v) => update('location', v)}>
                        <SelectTrigger data-testid="arch-select-location" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Budget (R)</Label>
                      <Input data-testid="arch-input-budget" type="number" value={form.budget} onChange={(e) => update('budget', e.target.value)} placeholder="Approximate budget" className="rounded-sm h-11" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Your Name</Label>
                      <Input data-testid="arch-input-name" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Optional" className="rounded-sm h-11" />
                    </div>
                  </div>
                  <Button data-testid="arch-start-btn" onClick={startSession} disabled={loading || !form.project_type || !form.location} className="w-full mt-4 bg-brand-dark text-white hover:bg-gray-800 rounded-sm h-12 font-body text-sm font-semibold uppercase tracking-wider">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Begin Guided Consultation <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </div>
              )}

              {/* STEP 1: Spatial Intent */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="font-heading text-xl font-medium mb-2">How do you experience space?</h2>
                    <p className="text-brand-dark/50 font-body text-sm">We're listening for what matters to you — not architectural jargon.</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">What spaces matter most in your daily life?</Label>
                    <Textarea data-testid="arch-important-spaces" value={form.important_spaces} onChange={(e) => update('important_spaces', e.target.value)}
                      placeholder="e.g. The kitchen is where everyone gathers. I need a quiet space to read. The garden is my sanctuary." className="rounded-sm min-h-[80px]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Where do you feel space is currently lacking?</Label>
                    <Textarea data-testid="arch-lacking-space" value={form.lacking_space} onChange={(e) => update('lacking_space', e.target.value)}
                      placeholder="e.g. We have no dedicated workspace. The living area feels cramped when we entertain." className="rounded-sm min-h-[80px]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Do you value openness or separation more?</Label>
                    <Select value={form.openness_preference} onValueChange={(v) => update('openness_preference', v)}>
                      <SelectTrigger data-testid="arch-openness" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open — I love connected, flowing spaces</SelectItem>
                        <SelectItem value="separated">Separated — I need defined, private rooms</SelectItem>
                        <SelectItem value="balanced">Balanced — Open communal areas, private retreats</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 2: Lifestyle */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="font-heading text-xl font-medium mb-2">Your lifestyle shapes your architecture.</h2>
                    <p className="text-brand-dark/50 font-body text-sm">These details help your architect design around how you actually live.</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Describe a typical day at home</Label>
                    <Textarea data-testid="arch-daily-routine" value={form.daily_routine} onChange={(e) => update('daily_routine', e.target.value)}
                      placeholder="e.g. Morning coffee on the patio. Kids play in the garden while I work from home. Evening meals together." className="rounded-sm min-h-[80px]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">How do you entertain?</Label>
                      <Select value={form.entertaining_style} onValueChange={(v) => update('entertaining_style', v)}>
                        <SelectTrigger data-testid="arch-entertaining" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minimal">Rarely — prefer quiet</SelectItem>
                          <SelectItem value="intimate">Small gatherings (4-8 people)</SelectItem>
                          <SelectItem value="regular">Regular entertaining (8-20)</SelectItem>
                          <SelectItem value="large">Large events (20+)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Work from home?</Label>
                      <Select value={form.work_style} onValueChange={(v) => update('work_style', v)}>
                        <SelectTrigger data-testid="arch-work" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No — I work elsewhere</SelectItem>
                          <SelectItem value="sometimes">Sometimes — need flexible space</SelectItem>
                          <SelectItem value="full_time">Full-time — need dedicated office</SelectItem>
                          <SelectItem value="multiple">Multiple people work from home</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">How important is outdoor living?</Label>
                    <Select value={form.outdoor_priority} onValueChange={(v) => update('outdoor_priority', v)}>
                      <SelectTrigger data-testid="arch-outdoor" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="essential">Essential — outdoor living is core to how we live</SelectItem>
                        <SelectItem value="important">Important — we enjoy outdoor spaces regularly</SelectItem>
                        <SelectItem value="nice">Nice to have — but not a priority</SelectItem>
                        <SelectItem value="minimal">Minimal — focus on indoor spaces</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 3: Constraints & Trade-offs */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="font-heading text-xl font-medium mb-2">Understanding your trade-offs.</h2>
                    <p className="text-brand-dark/50 font-body text-sm">
                      Given your budget, some choices have bigger impacts than others. This is not about limitations — it's about making informed decisions.
                    </p>
                  </div>

                  {tradeoffs && (
                    <div className="bg-brand-light rounded-sm p-5 border border-black/5">
                      <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">What your budget can build</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(tradeoffs).map(([level, data]) => (
                          <div key={level} className="bg-white rounded-sm p-3 text-center border border-black/5">
                            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 capitalize">{level}</p>
                            <p className="font-heading text-lg font-medium text-brand-dark mt-1">{data.affordable_sqm}m²</p>
                            <p className="text-xs text-brand-dark/40 mt-0.5">{formatZARFull(data.cost_per_sqm)}/m²</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-brand-dark/40 font-body mt-3 italic">
                        These are indicative ranges. Actual costs depend on many site-specific factors.
                      </p>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-2 block">What matters more to you?</Label>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-brand-light/50 rounded-sm border border-black/5">
                        <span className="text-sm font-body">More space (larger home, simpler finishes)</span>
                        <span className="text-brand-dark/30 text-xs">vs</span>
                        <span className="text-sm font-body">Higher quality (smaller, premium finishes)</span>
                      </div>
                    </div>
                    <Select value={form.size_priority || ''} onValueChange={(v) => update('size_priority', v)}>
                      <SelectTrigger data-testid="arch-size-priority" className="rounded-sm h-11 mt-2"><SelectValue placeholder="Select your preference" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="size">More space — I'll compromise on finishes</SelectItem>
                        <SelectItem value="quality">Higher quality — I'll accept a smaller footprint</SelectItem>
                        <SelectItem value="balanced">Balanced — I want both to be reasonable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 4: Readiness */}
              {step === 4 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="font-heading text-xl font-medium mb-2">How ready are you?</h2>
                    <p className="text-brand-dark/50 font-body text-sm">
                      This is not a test. It helps us understand where you are in your journey — and what to focus on next.
                    </p>
                  </div>
                  <div className="space-y-4">
                    {[
                      { key: 'has_site', label: 'I have a specific site or stand identified', desc: 'You know where you want to build or renovate.' },
                      { key: 'has_budget', label: 'I have a realistic budget range in mind', desc: 'You\'ve thought about what you can afford.' },
                      { key: 'has_timeline', label: 'I have a timeline for this project', desc: 'You know roughly when you want to start and finish.' },
                      { key: 'has_brief', label: 'I have a clear idea of what I need', desc: 'You can describe your requirements with some specificity.' },
                      { key: 'has_inspiration', label: 'I have reference images or inspiration', desc: 'You\'ve collected examples of what you like.' },
                    ].map(item => (
                      <label key={item.key} className="flex items-start gap-3 p-4 rounded-sm border border-black/5 hover:border-brand-accent/30 transition-colors cursor-pointer" data-testid={`arch-${item.key}`}>
                        <Checkbox checked={form[item.key]} onCheckedChange={(c) => update(item.key, c)} className="mt-0.5" />
                        <div>
                          <span className="text-sm font-body font-medium block">{item.label}</span>
                          <span className="text-xs text-brand-dark/40 font-body">{item.desc}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Email</Label>
                      <Input data-testid="arch-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" className="rounded-sm h-10" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Name</Label>
                      <Input data-testid="arch-name-final" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" className="rounded-sm h-10" />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 5: Summary */}
              {step === 5 && (
                <div className="space-y-6">
                  {!summary ? (
                    <div className="text-center py-12">
                      <h2 className="font-heading text-xl font-medium mb-4">Generating your Architect-Ready Summary</h2>
                      <p className="text-brand-dark/50 font-body text-sm mb-6">
                        We're synthesising your responses into a structured foundation for your architect.
                      </p>
                      <Loader2 className="w-8 h-8 animate-spin text-brand-accent mx-auto" />
                    </div>
                  ) : (
                    <div data-testid="architect-summary">
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h2 className="font-heading text-xl font-medium">Architect-Ready Summary</h2>
                      </div>

                      {/* Readiness Score */}
                      {readinessData && (
                        <div className="bg-brand-light rounded-sm p-5 border border-black/5 mb-6">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-heading uppercase tracking-wider text-brand-dark/50">Decision Confidence Index</span>
                            <span className="font-heading text-2xl font-medium text-brand-accent-dark">{readinessData.confidence_index}%</span>
                          </div>
                          <Progress value={readinessData.confidence_index} className="h-2" />
                        </div>
                      )}

                      {/* Spatial Signals */}
                      {spatialSignals.length > 0 && (
                        <div className="mb-6">
                          <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Architectural Signals Identified</h3>
                          <div className="space-y-1.5">
                            {spatialSignals.filter(s => s.trim()).map((signal, i) => (
                              <p key={i} className="text-sm font-body text-brand-dark/70 pl-3 border-l-2 border-brand-accent/40">{signal.replace(/^[-•]\s*/, '')}</p>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Summary */}
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-brand-accent" />
                          <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50">Structured Summary</h3>
                        </div>
                        <div className="prose prose-sm max-w-none text-brand-dark/70 font-body leading-relaxed whitespace-pre-wrap" data-testid="architect-summary-text">
                          {summary}
                        </div>
                      </div>

                      <Separator />

                      {/* Disclaimer + Next Steps */}
                      <div className="mt-6 space-y-4">
                        <div className="flex items-start gap-2 p-4 bg-brand-light rounded-sm border border-black/5">
                          <Shield className="w-4 h-4 text-brand-dark/30 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-brand-dark/40 font-body">
                            This prepares the ground — the architect creates the space.
                            Final architectural decisions require a registered architect. This output is a pre-design collaboration tool.
                          </p>
                        </div>

                        <div className="bg-brand-dark rounded-sm p-8 text-center">
                          <p className="text-brand-accent/60 font-heading italic text-sm mb-3">We create space — by removing uncertainty before design begins.</p>
                          <h3 className="font-heading text-lg font-medium text-white mb-4">Ready to speak with an architect?</h3>
                          <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/feasibility" data-testid="arch-cta-feasibility">
                              <Button className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                                Run Feasibility Check
                              </Button>
                            </Link>
                            <Link to="/cost-calculator" data-testid="arch-cta-cost">
                              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                                See Cost Reality
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {step > 0 && step < 5 && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-black/5">
              <Button data-testid="arch-prev-btn" variant="ghost" onClick={() => setStep(s => s - 1)} className="text-brand-dark/60 hover:text-brand-dark font-body text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              {step < 4 ? (
                <Button data-testid="arch-next-btn" onClick={() => submitStep(STEPS[step].id)} disabled={loading}
                  className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-sm font-semibold uppercase tracking-wider">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              ) : (
                <Button data-testid="arch-complete-btn" onClick={async () => { await submitStep(STEPS[step].id); await completeSession(); }} disabled={loading}
                  className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-sm font-semibold uppercase tracking-wider">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Generate Summary <CheckCircle className="w-4 h-4 ml-1" /></>}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Persistent disclaimer */}
        <p className="text-center text-brand-dark/30 text-xs font-body mt-8 max-w-lg mx-auto">
          {DISCLAIMER}
        </p>
      </div>
    </div>
  );
}
